import { create } from 'zustand';

/**
 * Persistent UI layout state: resizable column widths and per-panel collapse
 * flags. Kept separate from the workspace/analysis store so layout tweaks don't
 * re-render the heavy graph/map panels and don't leak into shared views.
 */
interface LayoutState {
  /** Left (events/sessions/laps) column width in px. */
  leftWidth: number;
  /** Right (map/channels) column width in px. */
  rightWidth: number;
  /** Collapsed flag per panel key (true = collapsed). */
  collapsed: Record<string, boolean>;
  /** When true the whole left (events/laps) sidebar is hidden. */
  leftCollapsed: boolean;
  setLeftWidth: (w: number) => void;
  setRightWidth: (w: number) => void;
  toggleCollapsed: (key: string) => void;
  toggleLeftCollapsed: () => void;
}

const LS_KEY = 'tla-layout';

function loadSaved(): Partial<LayoutState> {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? '{}') as Partial<LayoutState>;
  } catch {
    return {};
  }
}

function persist(s: LayoutState): void {
  try {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        leftWidth: s.leftWidth,
        rightWidth: s.rightWidth,
        collapsed: s.collapsed,
        leftCollapsed: s.leftCollapsed,
      }),
    );
  } catch {
    /* ignore quota / unavailable storage */
  }
}

const saved = loadSaved();

export const useLayoutStore = create<LayoutState>((set, get) => ({
  leftWidth: saved.leftWidth ?? 300,
  rightWidth: saved.rightWidth ?? 460,
  collapsed: saved.collapsed ?? {},
  leftCollapsed: saved.leftCollapsed ?? false,
  setLeftWidth: (w) => {
    set({ leftWidth: w });
    persist(get());
  },
  setRightWidth: (w) => {
    set({ rightWidth: w });
    persist(get());
  },
  toggleCollapsed: (key) => {
    set((s) => ({ collapsed: { ...s.collapsed, [key]: !s.collapsed[key] } }));
    persist(get());
  },
  toggleLeftCollapsed: () => {
    set((s) => ({ leftCollapsed: !s.leftCollapsed }));
    persist(get());
  },
}));
